import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';
import { AssistantService } from '@/cloud/services/assistant.service';
import { TenantService } from '@/cloud/services/tenant.service';
import { AssistantIssueProposal, AssistantIssueRef, IssueProposalBlock, PendingActionStatus } from '@/cloud/models/assistant.models';
import { issueRepoSegment } from '@/cloud/models/github.models';
import { TranslationService } from '@/layout/service/translation.service';
import { fill } from '@/layout/service/assistant.translations';
import { AssistantMarkdownPipe } from '../assistant-markdown.pipe';
import { clock, timeAgo } from '../assistant-format';
import { HOLD_MS } from './confirm-action.component';

const MAX_LABELS = 8;

/**
 * A drafted GitHub issue. The person can edit the title, text and labels, preview the issue as it will read
 * on GitHub (their text, the API's read-only evidence, the footer), or add it to an open issue it duplicates
 * instead. Holding the button files it; until then nothing reaches GitHub.
 */
@Component({
    selector: 'app-assistant-issue-proposal',
    standalone: true,
    imports: [TooltipModule, AssistantMarkdownPipe],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <section class="confirm-card issue-card" [attr.data-status]="state()" role="group" [attr.aria-label]="heading()">
            <header>
                <span class="confirm-icon"><i [class]="icon()" aria-hidden="true"></i></span>
                <span class="confirm-heading">{{ heading() }}</span>
                @if (p().serviceDesk) {
                    <span class="issue-chip">{{ t()('assistant.issue.serviceDesk') }}</span>
                }
            </header>
            <div class="issue-target">
                <a class="issue-repo" [href]="p().repositoryUrl" target="_blank" rel="noopener"><i class="pi pi-github" aria-hidden="true"></i>{{ p().repository }}</a>
                @if (p().problemSummary) {
                    <span class="issue-problem" [pTooltip]="p().problemSummary ?? ''" tooltipPosition="top"> <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>{{ problemText() }} </span>
                }
            </div>
            @if (transition(); as tr) {
                <div class="issue-transition" [attr.data-to]="tr.to">
                    <span [class]="tr.from === 'open' ? 'state-open' : 'state-closed'"><i [class]="tr.from === 'open' ? 'pi pi-circle' : 'pi pi-check-circle'" aria-hidden="true"></i>{{ stateText(tr.from) }}</span>
                    <i class="pi pi-arrow-right muted" aria-hidden="true"></i>
                    <span [class]="tr.to === 'open' ? 'state-open' : 'state-closed'"><i [class]="tr.to === 'open' ? 'pi pi-circle' : 'pi pi-check-circle'" aria-hidden="true"></i>{{ stateText(tr.to, tr.reason) }}</span>
                </div>
            }
            @if (p().reason) {
                <p class="confirm-reason">{{ p().reason }}</p>
            }

            @if (editable()) {
                @if (titleEditable()) {
                    <label class="issue-field">
                        <span>{{ t()('assistant.issue.title') }}</span>
                        <input type="text" maxlength="256" [value]="title()" (input)="title.set(value($event))" />
                    </label>
                }
                <div class="issue-tabs" role="tablist">
                    <button type="button" role="tab" [class.on]="!previewing()" [attr.aria-selected]="!previewing()" (click)="previewing.set(false)">{{ t()('assistant.issue.write') }}</button>
                    <button type="button" role="tab" [class.on]="previewing()" [attr.aria-selected]="previewing()" (click)="previewing.set(true)">{{ t()('assistant.issue.preview') }}</button>
                </div>
                @if (previewing()) {
                    <div class="issue-preview md-preview" [innerHTML]="preview() | assistantMarkdown"></div>
                } @else {
                    <textarea
                        class="issue-body"
                        rows="5"
                        maxlength="20000"
                        [value]="body()"
                        [placeholder]="mode() === 'issue' ? t()('assistant.issue.bodyPlaceholder') : t()('assistant.issue.commentPlaceholder')"
                        (input)="body.set(value($event))"
                    ></textarea>
                    @if (p().evidence) {
                        <details class="issue-evidence">
                            <summary><i class="pi pi-lock" aria-hidden="true"></i>{{ t()('assistant.issue.evidence') }}</summary>
                            <p class="muted small">{{ t()('assistant.issue.evidenceHint') }}</p>
                            <div class="md-preview" [innerHTML]="p().evidence | assistantMarkdown"></div>
                        </details>
                    }
                }

                @if (labelsEditable()) {
                    <div class="issue-labels" [attr.aria-label]="t()('assistant.issue.labels')">
                        @if (p().problemLabel) {
                            <span class="label-chip locked" [pTooltip]="t()('assistant.issue.problemLabel')" tooltipPosition="top"><i class="pi pi-lock" aria-hidden="true"></i>{{ p().problemLabel }}</span>
                        }
                        @for (label of p().removeLabels ?? []; track label) {
                            <span class="label-chip removed" [attr.aria-label]="fill(t()('assistant.issue.removing'), { label })">{{ label }}</span>
                        }
                        @for (label of labels(); track label) {
                            <span class="label-chip">
                                @if (p().kind === 'Update') {
                                    <i class="pi pi-plus" aria-hidden="true"></i>
                                }
                                {{ label }}
                                <button type="button" [attr.aria-label]="fill(t()('assistant.issue.removeLabel'), { label })" (click)="removeLabel(label)"><i class="pi pi-times" aria-hidden="true"></i></button>
                            </span>
                        }
                        @if (labels().length < maxLabels) {
                            <input class="label-input" type="text" maxlength="50" [placeholder]="t()('assistant.issue.addLabel')" (keydown.enter)="addLabel($event)" (blur)="addLabel($event)" />
                        }
                    </div>
                }
            }

            @if (p().related.length && state() === 'Proposed') {
                <div class="issue-related">
                    <div class="ab-title">{{ t()('assistant.issue.related') }}</div>
                    <ul>
                        @for (r of p().related; track r.number) {
                            <li [class.chosen]="commentOn() === r.number">
                                <i [class]="r.state === 'open' ? 'pi pi-circle state-open' : 'pi pi-check-circle state-closed'" aria-hidden="true"></i>
                                <a [href]="r.htmlUrl" target="_blank" rel="noopener">#{{ r.number }} {{ r.title }}</a>
                                @if (r.tracking) {
                                    <span class="issue-chip" [class.warn]="r.state !== 'open'">{{ r.state === 'open' ? t()('assistant.issue.tracks') : t()('assistant.issue.cameBack') }}</span>
                                }
                                <span class="muted small">{{ ago(r) }}</span>
                                @if (p().kind === 'Issue' && (r.state === 'open' || r.tracking)) {
                                    @if (commentOn() === r.number) {
                                        <button type="button" class="quick" (click)="commentOn.set(null)"><i class="pi pi-undo" aria-hidden="true"></i>{{ t()('assistant.issue.raiseInstead') }}</button>
                                    } @else if (r.state === 'open') {
                                        <button type="button" class="quick" (click)="commentOn.set(r.number)"><i class="pi pi-reply" aria-hidden="true"></i>{{ fill(t()('assistant.issue.addInstead'), { number: r.number }) }}</button>
                                    } @else {
                                        <button type="button" class="quick" (click)="commentOn.set(r.number)"><i class="pi pi-replay" aria-hidden="true"></i>{{ fill(t()('assistant.issue.reopenInstead'), { number: r.number }) }}</button>
                                    }
                                }
                            </li>
                        }
                    </ul>
                </div>
            }

            @switch (state()) {
                @case ('Proposed') {
                    <p class="muted small">{{ t()('assistant.issue.nothingSent') }}</p>
                    <div class="confirm-buttons">
                        <button
                            type="button"
                            class="hold-button"
                            [class.holding]="holding()"
                            [disabled]="busy() || !canFile()"
                            (pointerdown)="startHold($event)"
                            (pointerup)="cancelHold()"
                            (pointerleave)="cancelHold()"
                            (pointercancel)="cancelHold()"
                            (keydown)="onKeyDown($event)"
                            (keyup)="onKeyUp($event)"
                        >
                            <span class="hold-fill" [style.transition-duration.ms]="holding() ? holdMs : 150"></span>
                            <span class="hold-label"><i class="pi pi-github" aria-hidden="true"></i>{{ holding() ? t()('assistant.action.holding') : holdLabel() }}</span>
                        </button>
                        <button type="button" class="reject-button" [disabled]="busy()" (click)="reject()">{{ t()('assistant.action.reject') }}</button>
                        <span class="countdown muted" aria-live="off"><i class="pi pi-hourglass" aria-hidden="true"></i>{{ remaining() }}</span>
                    </div>
                }
                @case ('Confirming') {
                    <p class="muted small"><i class="pi pi-spin pi-spinner" aria-hidden="true"></i> {{ t()('assistant.issue.filing') }}</p>
                }
                @case ('Confirmed') {
                    <div class="issue-done" aria-live="polite">
                        <span class="issue-filed"><i class="pi pi-check-circle" aria-hidden="true"></i>{{ doneText() }}</span>
                        @if (p().resultUrl) {
                            <a class="quick" [href]="p().resultUrl" target="_blank" rel="noopener"><i class="pi pi-external-link" aria-hidden="true"></i>{{ t()('assistant.issue.openGitHub') }}</a>
                        }
                        @if (p().resultNumber && !p().serviceDesk) {
                            <button type="button" class="quick" (click)="openInPortal()"><i class="pi pi-github" aria-hidden="true"></i>{{ t()('assistant.issue.openPortal') }}</button>
                        }
                    </div>
                }
                @case ('Rejected') {
                    <p class="muted small">{{ t()('assistant.issue.rejected') }}</p>
                }
                @case ('Expired') {
                    <p class="muted small">{{ t()('assistant.issue.expired') }}</p>
                }
                @case ('Failed') {
                    <p class="confirm-error">{{ fill(t()('assistant.issue.failed'), { error: p().error ?? '—' }) }}</p>
                }
            }
            @if (error()) {
                <p class="confirm-error" role="alert">{{ error() }}</p>
            }
        </section>
    `,
    styleUrl: './blocks.scss'
})
export class AssistantIssueProposalComponent {
    readonly block = input.required<IssueProposalBlock>();

    private assistant = inject(AssistantService);
    private tenants = inject(TenantService);
    private router = inject(Router);
    readonly t = inject(TranslationService).t;
    readonly fill = fill;

    readonly holdMs = HOLD_MS;
    readonly maxLabels = MAX_LABELS;
    readonly holding = signal(false);
    readonly busy = signal(false);
    readonly error = signal<string | null>(null);
    readonly now = signal(Date.now());
    readonly previewing = signal(false);

    /** The person's working copy; sent with the confirm. */
    readonly title = signal('');
    readonly body = signal('');
    readonly labels = signal<string[]>([]);
    /** An open related issue chosen to add this to instead of raising a new one. */
    readonly commentOn = signal<number | null>(null);
    private seeded: string | null = null;
    private holdTimer: ReturnType<typeof setTimeout> | null = null;

    readonly p = computed<AssistantIssueProposal>(() => this.assistant.issueProposals()[this.block().proposal.id] ?? this.block().proposal);

    readonly state = computed<PendingActionStatus>(() => {
        const p = this.p();
        return p.status === 'Proposed' && Date.parse(p.expiresUtc) <= this.now() ? 'Expired' : p.status;
    });

    readonly editable = computed(() => this.state() === 'Proposed');

    readonly targetNumber = computed(() => this.commentOn() ?? (this.p().kind === 'Issue' ? null : (this.p().issueNumber ?? null)));

    /** What confirming will do, including a related issue the person chose instead of a new one. */
    readonly mode = computed<'issue' | 'comment' | 'reopen' | 'close' | 'update'>(() => {
        const p = this.p();
        const chosen = this.commentOn();
        if (chosen !== null) return p.related.find((r) => r.number === chosen)?.state === 'open' ? 'comment' : 'reopen';
        if (p.kind === 'Comment') return 'comment';
        if (p.kind === 'Update') return p.newState === 'open' ? 'reopen' : p.newState === 'closed' ? 'close' : 'update';
        return 'issue';
    });

    /** The state change, when there is one: from the issue's state when proposed to the one it will have. */
    readonly transition = computed<{ from: string; to: string; reason: string | null } | null>(() => {
        const p = this.p();
        switch (this.mode()) {
            case 'reopen':
                return { from: 'closed', to: 'open', reason: null };
            case 'close':
                return { from: p.issueState ?? 'open', to: 'closed', reason: p.stateReason ?? 'completed' };
            default:
                return null;
        }
    });

    readonly titleEditable = computed(() => this.mode() === 'issue' || (this.p().kind === 'Update' && this.commentOn() === null));
    readonly labelsEditable = computed(() => this.mode() === 'issue' || (this.p().kind === 'Update' && this.commentOn() === null));

    readonly heading = computed(() => {
        const p = this.p();
        const number = this.state() === 'Confirmed' ? p.issueNumber : this.targetNumber();
        if (!number) return fill(this.t()('assistant.issue.draftIssue'), { target: p.packageName || p.repository });
        const key = {
            comment: 'assistant.issue.draftComment',
            reopen: 'assistant.issue.reopen',
            close: 'assistant.issue.close',
            update: 'assistant.issue.update',
            issue: 'assistant.issue.draftComment'
        }[this.mode()];
        const title = this.relatedTitle(number);
        return fill(this.t()(key), { number, reason: this.reasonText(p.stateReason) }) + (title ? ` · ${title}` : '');
    });

    readonly icon = computed(() => {
        switch (this.state()) {
            case 'Confirmed':
                return 'pi pi-check-circle';
            case 'Confirming':
                return 'pi pi-spin pi-spinner';
            case 'Rejected':
            case 'Expired':
                return 'pi pi-minus-circle';
            case 'Failed':
                return 'pi pi-times-circle';
            default:
                return { issue: 'pi pi-github', comment: 'pi pi-reply', reopen: 'pi pi-replay', close: 'pi pi-check-square', update: 'pi pi-pencil' }[this.mode()];
        }
    });

    readonly problemText = computed(() => fill(this.t()('assistant.issue.problem'), { devices: this.p().affectedDevices }));

    readonly holdLabel = computed(() =>
        this.t()({ issue: 'assistant.issue.holdIssue', comment: 'assistant.issue.holdComment', reopen: 'assistant.issue.holdReopen', close: 'assistant.issue.holdClose', update: 'assistant.issue.holdUpdate' }[this.mode()])
    );

    readonly canFile = computed(() => {
        switch (this.mode()) {
            case 'issue':
                return this.title().trim().length > 0;
            case 'comment':
                return this.body().trim().length > 0 || !!this.p().evidence;
            default:
                return !this.titleEditable() || this.title().trim().length > 0;
        }
    });

    readonly remaining = computed(() => fill(this.t()('assistant.action.expiresIn'), { time: clock(Date.parse(this.p().expiresUtc) - this.now()) }));

    readonly doneText = computed(() => {
        const p = this.p();
        const key =
            p.kind === 'Comment' ? 'assistant.issue.commented' : p.kind === 'Update' ? (p.newState === 'open' ? 'assistant.issue.reopened' : p.newState === 'closed' ? 'assistant.issue.closed' : 'assistant.issue.updated') : 'assistant.issue.filed';
        return fill(this.t()(key), { number: p.resultNumber ?? '?' });
    });

    /** The issue as GitHub will show it: the person's text, the evidence, then the footer. */
    readonly preview = computed(() => {
        const p = this.p();
        const head = this.mode() === 'issue' ? `### ${this.title().trim()}\n\n` : '';
        const footer = p.serviceDesk ? '_Raised by the proAV.Net service desk with the proAV.Net assistant, and confirmed by a person before it was filed._' : '_Raised with the proAV.Net assistant, and confirmed by a person before it was filed._';
        return [head + this.body().trim(), p.evidence ?? '', '---\n' + footer].filter((part) => part.trim().length > 0).join('\n\n');
    });

    constructor() {
        const tick = setInterval(() => this.now.set(Date.now()), 1000);
        inject(DestroyRef).onDestroy(() => {
            clearInterval(tick);
            this.clearHold();
        });

        // Seed the working copy once per proposal, so live updates don't overwrite the person's edits.
        effect(() => {
            const p = this.p();
            untracked(() => {
                if (this.seeded === p.id) return;
                this.seeded = p.id;
                this.title.set(p.title);
                this.body.set(p.body);
                this.labels.set([...(p.labels ?? [])]);
            });
        });
    }

    value(event: Event): string {
        return (event.target as HTMLInputElement | HTMLTextAreaElement).value;
    }

    addLabel(event: Event): void {
        const inputEl = event.target as HTMLInputElement;
        const label = inputEl.value.trim();
        if (event instanceof KeyboardEvent) event.preventDefault();
        inputEl.value = '';
        if (!label || label.toLowerCase().startsWith('proav:')) return;
        this.labels.update((list) => (list.some((l) => l.toLowerCase() === label.toLowerCase()) || list.length >= MAX_LABELS ? list : [...list, label]));
    }

    removeLabel(label: string): void {
        this.labels.update((list) => list.filter((l) => l !== label));
    }

    stateText(state: string, reason: string | null = null): string {
        const text = this.t()(state === 'open' ? 'assistant.issues.open' : 'assistant.issues.closed');
        return reason ? `${text} · ${this.reasonText(reason)}` : text;
    }

    reasonText(reason: string | null | undefined): string {
        return this.t()(reason === 'not_planned' ? 'assistant.issue.reasonNotPlanned' : 'assistant.issue.reasonCompleted');
    }

    ago(r: AssistantIssueRef): string {
        const time = timeAgo(r.updatedUtc, this.now());
        return time ? fill(this.t()('assistant.issues.updated'), { time }) : '';
    }

    startHold(event?: Event): void {
        if (this.busy() || this.state() !== 'Proposed' || !this.canFile()) return;
        event?.preventDefault();
        this.clearHold();
        this.holding.set(true);
        this.holdTimer = setTimeout(() => {
            this.holdTimer = null;
            this.holding.set(false);
            this.confirm();
        }, HOLD_MS);
    }

    cancelHold(): void {
        if (this.holdTimer) this.clearHold();
    }

    onKeyDown(event: KeyboardEvent): void {
        if ((event.key === 'Enter' || event.key === ' ') && !event.repeat) this.startHold(event);
    }

    onKeyUp(event: KeyboardEvent): void {
        if (event.key === 'Enter' || event.key === ' ') this.cancelHold();
    }

    async confirm(): Promise<void> {
        this.busy.set(true);
        this.error.set(null);
        try {
            const p = this.p();
            const commentOn = this.commentOn();
            await this.assistant.decideIssue(p.id, 'confirm', {
                title: this.titleEditable() ? this.title().trim() : undefined,
                body: this.body(),
                labels: this.labelsEditable() ? this.labels() : undefined,
                commentOn: commentOn ?? undefined
            });
        } catch (err) {
            this.error.set(AssistantIssueProposalComponent.message(err) ?? 'Could not file it. Try again.');
        } finally {
            this.busy.set(false);
        }
    }

    async reject(): Promise<void> {
        this.clearHold();
        this.busy.set(true);
        this.error.set(null);
        try {
            await this.assistant.decideIssue(this.p().id, 'reject');
        } catch (err) {
            this.error.set(AssistantIssueProposalComponent.message(err) ?? 'Could not decline. Try again.');
        } finally {
            this.busy.set(false);
        }
    }

    openInPortal(): void {
        const p = this.p();
        if (!p.resultNumber) return;
        if (p.namespaceId !== this.tenants.selectedTenantId()) this.tenants.select(p.namespaceId);
        this.router.navigate(['/issues', issueRepoSegment(p.repository), p.resultNumber]);
    }

    private relatedTitle(number: number): string | null {
        const p = this.p();
        return p.related.find((r) => r.number === number)?.title ?? (p.issueNumber === number ? (p.issueTitle ?? null) : null);
    }

    private clearHold(): void {
        if (this.holdTimer) clearTimeout(this.holdTimer);
        this.holdTimer = null;
        this.holding.set(false);
    }

    private static message(err: unknown): string | null {
        const e = err as { error?: { error?: string; message?: string } | string } | null;
        if (typeof e?.error === 'string') return e.error;
        return e?.error?.error ?? e?.error?.message ?? null;
    }
}
